import { useState, useEffect, useCallback, useRef } from 'react';
import { Download, Upload, AlertTriangle, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Ind({ label, value }) {
  return (
    <div className="border border-white/5 rounded p-3 bg-white/[0.02]">
      <p className="text-white/30 text-[11px] mb-1">{label}</p>
      <p className="text-white text-xl font-light">{value}</p>
    </div>
  );
}

// Minimal CSV parser (handles quoted fields).
function parseCSV(text) {
  const rows = [];
  let i = 0, field = '', row = [], inQ = false;
  while (i < text.length) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
      if (ch === '"') { inQ = false; i++; continue; }
      field += ch; i++; continue;
    }
    if (ch === '"') { inQ = true; i++; continue; }
    if (ch === ',') { row.push(field); field = ''; i++; continue; }
    if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.length > 1 || row[0] !== '') rows.push(row);
      row = []; i++; continue;
    }
    field += ch; i++;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1).map((r) => Object.fromEntries(headers.map((h, idx) => [h, r[idx] ?? ''])));
}

export default function PanelControl({ token }) {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [cfgMsg, setCfgMsg] = useState(null);
  const [importMsg, setImportMsg] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const [d, c] = await Promise.all([
      fetch(`${BACKEND_URL}/api/admin/advisor-dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${BACKEND_URL}/api/admin/config`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    if (d.ok) setData(await d.json());
    if (c.ok) setConfig(await c.json());
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const exportCSV = async () => {
    const res = await fetch(`${BACKEND_URL}/api/admin/export-candidates`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'candidatos.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportMsg('Importando…');
    const text = await file.text();
    const rows = parseCSV(text);
    const res = await fetch(`${BACKEND_URL}/api/admin/candidates/import`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ rows }),
    });
    const j = await res.json();
    setImportMsg(res.ok ? `Importados ${j.imported} candidatos` : (j.detail || 'Error'));
    load();
  };

  const saveConfig = async () => {
    setCfgMsg(null);
    const res = await fetch(`${BACKEND_URL}/api/admin/config`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(config),
    });
    const j = await res.json();
    if (res.ok) { setConfig(j); setCfgMsg({ type: 'ok', text: 'Configuración guardada' }); }
    else setCfgMsg({ type: 'err', text: j.detail || 'Error' });
  };

  if (!data || !config) return <div className="text-white/30 text-sm py-12 text-center">Cargando panel…</div>;

  const inp = 'w-16 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs outline-none';

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {data.alertas.length > 0 && (
        <div className="space-y-2" data-testid="panel-alertas">
          {data.alertas.map((a) => (
            <div key={a} className="flex items-center gap-2 bg-red-400/10 border border-red-400/20 rounded px-3 py-2 text-red-300 text-xs">
              <AlertTriangle size={14} />{a}
            </div>
          ))}
        </div>
      )}

      {/* Indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <Ind label="Total candidatos" value={data.total} />
        <Ind label="Admitidos" value={data.admitidos} />
        <Ind label="Tasa admisión" value={`${data.tasa_admision}%`} />
        <Ind label="Fit medio admitidos" value={data.fit_medio_admitidos} />
        <Ind label="Min. medios verif." value={data.minutos_medios_verificacion} />
        <Ind label="Fichas incompletas" value={data.fichas_incompletas} />
        <Ind label="Divergencias" value={data.divergencias} />
        <Ind label="Días espera medio" value={data.dias_espera_medio} />
        <Ind label="Celdas críticas s/cubrir" value={data.celdas_criticas_sin_cubrir} />
      </div>

      {/* Estados */}
      <div className="border border-white/5 rounded p-4">
        <p className="text-white/50 text-xs font-medium mb-3">Candidatos por estado</p>
        <div className="flex flex-wrap gap-3">
          {Object.entries(data.por_estado).map(([k, v]) => (
            <div key={k} className="text-xs"><span className="text-white/40">{k}:</span> <span className="text-white">{v}</span></div>
          ))}
        </div>
      </div>

      {/* Export / Import */}
      <div className="border border-white/5 rounded p-4 flex flex-wrap items-center gap-3">
        <button onClick={exportCSV} data-testid="panel-export" className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-3 py-2 text-white/70 hover:text-white text-xs">
          <Download size={13} />Exportar CSV
        </button>
        <button onClick={() => fileRef.current.click()} data-testid="panel-import" className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-3 py-2 text-white/70 hover:text-white text-xs">
          <Upload size={13} />Importar CSV
        </button>
        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onImport} />
        {importMsg && <span className="text-white/50 text-xs">{importMsg}</span>}
      </div>

      {/* Config editor */}
      <div className="border border-white/5 rounded p-4">
        <p className="text-white/50 text-xs font-medium mb-3">Parámetros (config)</p>
        <div className="flex flex-wrap gap-4 items-end">
          {Object.keys(config.pesos).map((k) => (
            <div key={k}><span className="text-white/40 text-[10px] block mb-1">{k}</span>
              <input type="number" className={inp} value={config.pesos[k]} onChange={(e) => setConfig((c) => ({ ...c, pesos: { ...c.pesos, [k]: parseInt(e.target.value || '0', 10) } }))} /></div>
          ))}
          <div><span className="text-white/40 text-[10px] block mb-1">Σ pesos</span><span className={`text-sm ${Object.values(config.pesos).reduce((a, b) => a + b, 0) === 100 ? 'text-emerald-400' : 'text-red-400'}`}>{Object.values(config.pesos).reduce((a, b) => a + b, 0)}</span></div>
        </div>
        <div className="flex flex-wrap gap-4 items-end mt-3">
          {['umbral_admision', 'umbral_condicionado', 'max_roles_referencia', 'minutos_objetivo_verificacion', 'alerta_tasa_admision_max', 'alerta_tasa_admision_min', 'dias_revalidacion'].map((k) => (
            <div key={k}><span className="text-white/40 text-[10px] block mb-1">{k}</span>
              <input type="number" className={inp} value={config[k]} onChange={(e) => setConfig((c) => ({ ...c, [k]: parseInt(e.target.value || '0', 10) }))} /></div>
          ))}
        </div>
        <div className="flex flex-wrap gap-4 items-end mt-3 pt-3 border-t border-white/5">
          <div>
            <span className="text-white/40 text-[10px] block mb-1">Email de aviso (nuevos registros)</span>
            <input type="email" className="w-64 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs outline-none"
              value={config.aviso_email || ''} onChange={(e) => setConfig((c) => ({ ...c, aviso_email: e.target.value }))}
              data-testid="config-aviso-email" />
          </div>
          <div>
            <span className="text-white/40 text-[10px] block mb-1">Plazo de validación (horas)</span>
            <input type="number" className={inp} value={config.plazo_horas ?? 48}
              onChange={(e) => setConfig((c) => ({ ...c, plazo_horas: parseInt(e.target.value || '0', 10) }))}
              data-testid="config-plazo-horas" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button onClick={saveConfig} data-testid="panel-save-config" className="flex items-center gap-2 bg-[#C58C40] text-white rounded px-4 py-2 text-xs hover:bg-[#b07b35]"><Save size={13} />Guardar config</button>
          {cfgMsg && <span className={`text-xs ${cfgMsg.type === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>{cfgMsg.text}</span>}
        </div>
      </div>
    </div>
  );
}
